// src/screens/admin/auth/InviteRegister.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import usePublicUserInvitesHook from "../../../api/user-invites";

export default function InviteRegister() {
    const { t } = useTranslation();
    const [sp] = useSearchParams();
    const token = sp.get('token') || sp.get('invite'); // accept both
    const navigate = useNavigate();

    const { useValidateInviteToken, registerWithInvite } = usePublicUserInvitesHook();
    const { data, isLoading, isError, error } = useValidateInviteToken(token);

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [doneMsg, setDoneMsg] = useState('');
    const [errMsg, setErrMsg] = useState('');

    useEffect(() => {
        if (data?.email) setEmail(data.email);
    }, [data?.email]);

    const emailLocked = useMemo(() => Boolean(data?.email), [data]);
    const ok = data?.ok && !data?.expired && !data?.consumed;

    const { mutateAsync: registerAsync, isLoading: isRegistering } = registerWithInvite;

    const submitLocal = async (e) => {
        e.preventDefault();
        setErrMsg('');
        setDoneMsg('');
        try {
            await registerAsync({ token, name, email, password });
            setDoneMsg(t('Registration submitted. Awaiting approval.'));
        } catch (e2) {
            setErrMsg(e2?.error || t('Registration failed.'));
        }
    };

    return (
        <>
            <HelmetProvider>
                <Helmet>
                    <title>{t('Accept Invite')}</title>
                </Helmet>
            </HelmetProvider>

            <div className="container py-4" style={{ maxWidth: 680 }}>
                <h3 className="fw-light text-center mb-3">{t('Accept Invite')}</h3>

                {isLoading ? (
                    <div className="text-muted">{t('Checking invite...')}</div>
                ) : isError || !token || !ok ? (
                    <div className="alert alert-danger">
                        {error?.message ||
                            (data?.expired
                                ? t('This invite has expired.')
                                : data?.consumed
                                    ? t('This invite has already been used.')
                                    : t('Invite not valid.'))}
                    </div>
                ) : (
                    <>
                        <div className="card mb-3">
                            <div className="card-body">
                                <div className="mb-1">
                                    <strong>{t('Invite for')}</strong>{' '}
                                    {data?.email ? <code>{data.email}</code> : <em>{t('any email')}</em>}
                                </div>
                                {data?.expiresAt && (
                                    <div className="text-muted small">
                                        {t('Expires')}: {new Date(data.expiresAt).toLocaleString()}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Google Sign-In via your existing login page */}
                        <div className="mb-4">
                            <div className="mb-2">{t('Sign in with Google')}:</div>
                            <button
                                className="btn btn-outline-primary w-100"
                                onClick={() => navigate(`/admin/login?invite=${encodeURIComponent(token)}`)}
                            >
                                {t('Continue with Google')}
                            </button>
                        </div>

                        <div className="text-center my-2 text-muted">{t('or')}</div>

                        {/* Local registration form */}
                        {doneMsg ? (
                            <div className="alert alert-success">{doneMsg}</div>
                        ) : (
                            <form onSubmit={submitLocal} className="card">
                                <div className="card-body">
                                    <div className="mb-3">
                                        <label className="form-label">{t('Name')}</label>
                                        <input
                                            className="form-control"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            required
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">{t('Email')}</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            disabled={emailLocked}
                                        />
                                        {emailLocked && (
                                            <div className="form-text">
                                                {t('Email is locked to the invited address.')}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">{t('Password')}</label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            minLength={6}
                                        />
                                    </div>

                                    {errMsg && <div className="alert alert-danger">{errMsg}</div>}

                                    <button className="btn btn-primary w-100" disabled={isRegistering}>
                                        {isRegistering ? (
                                            <span className="spinner-border spinner-border-sm" />
                                        ) : (
                                            t('Submit for Approval')
                                        )}
                                    </button>

                                    <div className="form-text mt-2">
                                        {t('An admin will review and approve your access.')}
                                    </div>
                                </div>
                            </form>
                        )}
                    </>
                )}
            </div>
        </>
    );
}
